import { SurveyModel } from "@rubi/api-generated";

import { ILocaleObject } from "@/components/online-reporting/types";

export const extractSurveyTitleFromModel = (model: SurveyModel) => {
  if (typeof model.configuration === "string") {
    const parsedConfig = JSON.parse(model.configuration);
    return parsedConfig.title || model.id;
  }
  if (typeof model.configuration === "object") {
    const title = model.configuration.title;
    if (typeof title === "string") {
      return title;
    } else if (title !== null && typeof title === "object") {
      const localeTitle = title as ILocaleObject;
      return `${localeTitle.default} / ${localeTitle.fr}`;
    }
  }

  return model.id;
};

export const extractLocaleStringFromField = (
  field: string | ILocaleObject,
  locale?: string,
) => {
  if (typeof field === "string") {
    return field;
  }
  if (typeof field === "object") {
    if (locale === "fr") {
      return field.fr;
    }
    return field.default;
  }
};

const questionsJSON = [
  {
    name: "person-reportingParty_location-home_lookupNotFound",
    type: "checkbox",
    choices: [
      {
        text: " I can't find the address",
        value: "Lookup Address Not Found ",
      },
    ],
    titleLocation: "hidden",
  },
  {
    name: "person-reportingParty_location-home_region",
    type: "dropdown",
    title: "Community",
    choices: [
      "Beausoleil First Nation (Christian Island)",
      "Biigtigong Nishnaabeg (Pic River)",
      "Biinjitwaabik Zaaging Anishinaabek (Rocky Bay)",
      "Curve Lake",
      "Dokis First Nation",
      "Fort William First Nation",
      "Garden River",
      "Ginoogaming (Long Lac #77)",
      "Kettle & Stony Point",
      "Magnetawan First Nation",
      "Netmizaagaming Nishnaabeg (Pic Mobert)",
      "Nipissing First Nation",
      "Sagamok Anishnawbek",
      "Shawanaga First Nation",
      "Wahnapitae First Nation",
      "Wasauksing First Nation",
    ],
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    isRequired: true,
  },
  {
    name: "person-reportingParty_location-home_line1",
    type: "text",
    title: "Street Number and Name",
    maxLength: 70,
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    description: "Example: 34 Hiawatha Dr.",
    requiredErrorText:
      "Please start typing an address and select from the dropdown (pre-filled addresses are not accepted)",
  },
  {
    name: "person-reportingParty_location-home_line2",
    type: "text",
    title: "Apt, suite, etc. (optional)",
    maxLength: 70,
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    description: "Example: Apt. 12",
    requiredErrorText:
      "Please start typing an address and select from the dropdown (pre-filled addresses are not accepted)",
  },
  {
    name: "person-reportingParty_location-home_description",
    type: "text",
    title: "Location Description",
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    description: "Example: Near the Pow Wow grounds",
  },
];

const condensedQuestionsJSON = [
  {
    type: "dropdown",
    name: "person-reportingParty_location-home_region",
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    title: "COMMUNITY",
    isRequired: true,
    choices: [
      "Beausoleil First Nation (Christian Island)",
      "Biigtigong Nishnaabeg (Pic River)",
      "Biinjitwaabik Zaaging Anishinaabek (Rocky Bay)",
      "Curve Lake",
      "Dokis First Nation",
      "Fort William First Nation",
      "Garden River",
      "Ginoogaming (Long Lac #77)",
      "Kettle & Stony Point",
      "Magnetawan First Nation",
      "Netmizaagaming Nishnaabeg (Pic Mobert)",
      "Nipissing First Nation",
      "Sagamok Anishnawbek",
      "Shawanaga First Nation",
      "Wahnapitae First Nation",
      "Wasauksing First Nation",
    ],
  },
  {
    type: "text",
    name: "person-reportingParty_location-home_line1",
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    title: "STREET NUMBER AND NAME",
    requiredErrorText:
      "Please start typing an address and select from the dropdown (pre-filled addresses are not accepted)",
    maxLength: 70,
  },
  {
    type: "text",
    name: "person-reportingParty_location-home_line2",
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    startWithNewLine: false,
    title: "UNIT",
    requiredErrorText:
      "Please start typing an address and select from the dropdown (pre-filled addresses are not accepted)",
    maxLength: 70,
  },
  {
    type: "text",
    name: "person-reportingParty_location-home_description",
    visibleIf:
      "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
    title: "LOCATION DESCRIPTION",
  },
];

const condensedOptionalHeader = {
  type: "html",
  name: "lookupNotFoundHeader",
  visibleIf:
    "{panel.person-involved_location-home_lookupNotFound} = ['Lookup Address Not Found ']",
  html: '<font size="1">HOME ADDRESS</font>',
};

const originalPrefix = "person-reportingParty_location-home";
const topVisibleIf =
  "{person-reportingParty_location-home_lookupNotFound} empty";
const topResetValueIf =
  "{person-reportingParty_location-home_lookupNotFound} = ['Lookup Address Not Found ']";

const questionsToReplace = [
  {
    key: "person-reportingParty_location-home_address",
    prefix: "person-reportingParty_location-home",
  },
  {
    key: "business-reportingParty_location_address",
    prefix: "business-reportingParty_location",
    checkboxVisibleIf: "{person-reportingParty_reportingForEmployer} = true",
    topVisibleIfOverride:
      "{person-reportingParty_reportingForEmployer} = true and {business-reportingParty_location_lookupNotFound} empty",
  },
  { key: "incident_location_address", prefix: "incident_location" },
  {
    key: "person-involved_location-home_address",
    prefix: "panel.person-involved_location-home",
    checkboxVisibleIf: "{panel.person-involved_involvement} notempty",
    topVisibleIfOverride:
      "{panel.person-involved_involvement} notempty and {panel.person-involved_location-home_lookupNotFound} empty",
    condensedHeader: "HOME ADDRESS",
  },
  {
    key: "person-involved_location-work_address",
    prefix: "panel.person-involved_location-work",
    checkboxVisibleIf: "{panel.person-involved_involvement} notempty",
    topVisibleIfOverride:
      "{panel.person-involved_involvement} notempty and {panel.person-involved_location-work_lookupNotFound} empty",
    condensedHeader: "WORK ADDRESS",
  },
];

export const replaceAddressQuestionsWithLookupNotFounds = (
  surveyJSObject: any,
  condensed: boolean = false,
) => {
  const newSurveyJSObject = { ...surveyJSObject };

  const processObject = (obj: any, parentArray: any) => {
    if (Array.isArray(obj)) {
      obj.forEach((item) => processObject(item, obj));
    } else if (typeof obj === "object" && obj !== null) {
      if (obj.name) {
        const questionToReplace = questionsToReplace.find(
          (q) => q.key === obj.name,
        );
        if (questionToReplace) {
          // Replace visibleIf and resetValueIf fields
          obj.visibleIf =
            questionToReplace.topVisibleIfOverride ||
            topVisibleIf.replace(originalPrefix, questionToReplace.prefix);
          obj.resetValueIf = topResetValueIf.replace(
            originalPrefix,
            questionToReplace.prefix,
          );

          // Insert additional questions
          const additionalQuestions = (
            condensed ? condensedQuestionsJSON : questionsJSON
          ).map((question) => {
            const regex = new RegExp(originalPrefix, "g");

            if (
              questionToReplace.checkboxVisibleIf &&
              question.type === "checkbox"
            ) {
              question.visibleIf = questionToReplace.checkboxVisibleIf;
            }

            const newQuestion = JSON.stringify(question).replace(
              regex,
              questionToReplace.prefix,
            );
            return JSON.parse(newQuestion);
          });

          if (questionToReplace.condensedHeader && condensed) {
            const regex = new RegExp(originalPrefix, "g");

            additionalQuestions.unshift({
              ...condensedOptionalHeader,
              name: `lookupNotFoundHeader-${questionToReplace.prefix}`,
              visibleIf: condensedOptionalHeader.visibleIf.replace(
                regex,
                questionToReplace.prefix,
              ),
              html: `<font size="1">${questionToReplace.condensedHeader}</font>`,
            });
          }

          // Assuming the object is part of an array, insert additional questions
          const index = parentArray.indexOf(obj);
          parentArray[index] = obj;
          parentArray.splice(index + 1, 0, ...additionalQuestions);
        }
      }
      // Recursively process each property
      Object.values(obj).forEach(processObject);
    }
  };

  processObject(newSurveyJSObject, null);
  return newSurveyJSObject;
};

/**
 * Formats a survey version number for display from major and minor version fields
 */
export const formatVersionNumber = (
  version:
    | { major_version?: number | null; minor_version?: number | null }
    | null
    | undefined,
): string => {
  if (!version) return "draft";

  if (
    version.major_version !== null &&
    version.major_version !== undefined &&
    version.minor_version !== null &&
    version.minor_version !== undefined
  ) {
    return `${version.major_version}.${version.minor_version}`;
  }

  return "draft";
};
